import React from "react";
import cn from "../helpers/cn";

export type IconColor =
  | "inherit"
  | "primary"
  | "secondary"
  | "action"
  | "disabled"
  | "error";

export type IconFontSize =
  | "default"
  | "inherit"
  | "small"
  | "medium"
  | "large";

export interface IconProps extends Omit<React.SVGAttributes<SVGSVGElement>, "color"> {
  color?: IconColor;
  fontSize?: IconFontSize;
  htmlColor?: string;
  titleAccess?: string;
}

const fontSizeClasses: Record<IconFontSize, string> = {
  default: "text-[24px] w-[1em] h-[1em]",
  medium: "text-[24px] w-[1em] h-[1em]",
  small: "text-[20px] w-[1em] h-[1em]",
  large: "text-[35px] w-[1em] h-[1em]",
  inherit: "text-[inherit] w-[1em] h-[1em]",
};

const colorClasses: Record<IconColor, string> = {
  inherit: "text-current",
  primary: "text-primary",
  secondary: "text-secondary",
  action: "text-[rgba(0,0,0,0.54)]",
  disabled: "text-[rgba(0,0,0,0.26)]",
  error: "text-[#f44336]",
};

// React.ElementType permite passar Componentes Funcionais ou ForwardRefComponents
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyIconComponent = React.ElementType<any>;

// Renomeei de HeroIcon para SvgComponent para fazer sentido em ambos os casos
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
function createIcon(SvgComponent: AnyIconComponent, displayName: string) {
  const Icon = React.forwardRef<SVGSVGElement, IconProps>((props, ref) => {
    const {
      color = "inherit",
      fontSize = "default",
      htmlColor,
      titleAccess,
      className,
      style,
      ...other
    } = props;

    return (
      <SvgComponent
        ref={ref}
        title={titleAccess}
        focusable="false"
        aria-hidden={titleAccess ? undefined : true}
        role={titleAccess ? "img" : undefined}
        className={cn(
          "inline-block shrink-0 select-none",
          "transition-[fill,stroke] duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]",
          fontSizeClasses[fontSize],
          htmlColor ? undefined : colorClasses[color],
          className
        )}
        style={htmlColor ? { color: htmlColor, ...style } : style}
        {...other}
      />
    );
  });

  Icon.displayName = displayName;

  return Icon;
}

export default createIcon;